import { useLayoutEffect, useState, type RefObject } from 'react';

import { measureDropdown, type DropdownPosition } from './dropdownPosition';

/**
 * LOKALE: zet een dropdown-lijst (in een portal, `position: fixed`) vast aan zijn trigger — gedeeld door
 * `Select` en `Combobox`.
 *
 * **Waarom de scroll niet via state loopt.** De lijst staat in `document.body`, buiten de scrollende
 * container van de pagina, en volgt de trigger dus alleen als we hem na elke scroll opnieuw plaatsen. Deden
 * we dat met `setState`, dan wachtte de nieuwe positie op een React-render en liep de lijst zichtbaar achter
 * op de rest van de pagina. Daarom schrijft de scroll-handler de positie **direct op het element**: dat is
 * dezelfde frame als de scroll zelf.
 *
 * De state bevat alleen de positie bij openen en na een resize. Dat botst niet met de directe schrijfacties:
 * React schrijft een stijl alleen opnieuw als de waarde in de props verandert, en bij een scroll verandert de
 * state niet.
 *
 * Een scroll *in* de lijst zelf (de optie-lijst heeft een eigen scrollbalk) verplaatst de trigger niet en
 * wordt overgeslagen.
 */
export function useAnchoredDropdown(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  dropdownRef: RefObject<HTMLElement | null>,
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const measure = (): DropdownPosition | null =>
      anchorRef.current ? measureDropdown(anchorRef.current.getBoundingClientRect()) : null;

    setPosition(measure());

    const onResize = () => setPosition(measure());
    const onScroll = (event: Event) => {
      const dropdown = dropdownRef.current;
      if (dropdown === null || (event.target instanceof Node && dropdown.contains(event.target))) return;

      const next = measure();
      if (next !== null) applyPosition(dropdown, next);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [open, anchorRef, dropdownRef]);

  return position;
}

/** Schrijf een positie rechtstreeks op het element; het ongebruikte randpaar (boven/onder) wordt leeggemaakt. */
export function applyPosition(element: HTMLElement, position: DropdownPosition): void {
  const px = (value: number | undefined) => (value === undefined ? '' : `${value}px`);

  element.style.top = px(position.top);
  element.style.bottom = px(position.bottom);
  element.style.left = px(position.left);
  element.style.width = px(position.width);
  element.style.maxHeight = px(position.maxHeight);
}
